import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/delete-account")({
  head: () => ({
    meta: [
      { title: "Delete Your Like2Fit Account" },
      {
        name: "description",
        content:
          "How to delete your Like2Fit account and personal data — in-app or by email — what we delete, what we retain, and how to cancel App Store or Google Play subscriptions.",
      },
      { property: "og:title", content: "Delete Your Like2Fit Account" },
      {
        property: "og:description",
        content:
          "How to delete your Like2Fit account and personal data — in-app or by email — what we delete, what we retain, and how to cancel App Store or Google Play subscriptions.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://like2fit.com/delete-account" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://like2fit.com/delete-account" }],
  }),
  component: DeleteAccountPage,
});

const EMAIL = "support@like2fit.com";

function Section({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold">
        {n}. {title}
      </h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed">{children}</div>
    </section>
  );
}

function DeleteAccountPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back
        </Link>

        <h1 className="text-3xl font-semibold mt-6">Delete Your Like2Fit Account</h1>

        <div className="mt-6 text-[15px] leading-relaxed">
          <p>
            You can delete your Like2Fit account and personal data at any time. This
            page explains how to do it, what we remove, and what we&apos;re legally
            required to retain.
          </p>
        </div>

        <Section n={1} title="In the app (fastest)">
          <ol className="list-decimal pl-6 space-y-1">
            <li>Open Like2Fit and sign in</li>
            <li>Go to the Account tab</li>
            <li>Tap &ldquo;Delete account&rdquo;</li>
            <li>Confirm deletion</li>
            <li>Your account and all associated data are removed immediately.</li>
          </ol>
        </Section>

        <Section n={2} title="By email">
          <p>
            If you cannot access the app, email{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>{" "}
            from the email address on your account. Include the subject{" "}
            <strong>&ldquo;Account deletion request&rdquo;</strong>. We will delete
            your account within 30 days and confirm by reply.
          </p>
        </Section>

        <Section n={3} title="What we delete">
          <p>When your account is deleted, we permanently remove:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Your profile information (name, email, physical characteristics if
              provided)
            </li>
            <li>Your saved outfit analyses and lookbooks</li>
            <li>Your wardrobe items and preferences</li>
            <li>Your subscription and purchase history from our systems</li>
            <li>Your selfie (if uploaded for personalisation)</li>
            <li>Your location data (if you enabled Shops near me)</li>
            <li>Any personal identifiers linking you to activity logs</li>
          </ul>
        </Section>

        <Section n={4} title="What we retain">
          <p>
            For legal and regulatory compliance we retain the following for the
            periods stated:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Anonymised aggregate analytics: indefinitely (cannot be linked to you)
            </li>
            <li>Financial transaction records: 7 years (UK/EU tax law requirement)</li>
            <li>Fraud prevention data: 6 years (regulatory requirement)</li>
            <li>
              Backups: purged from our backup systems within 90 days of deletion
            </li>
          </ul>
        </Section>

        <Section n={5} title="App Store subscriptions">
          <p>
            Note: deleting your Like2Fit account does not automatically cancel Apple
            or Google Play subscriptions. Cancel your subscription separately via:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Apple: Settings → Apple ID → Subscriptions</li>
            <li>
              Google Play: Play Store → Menu → Payments &amp; subscriptions →
              Subscriptions
            </li>
          </ul>
        </Section>

        <Section n={6} title="Questions">
          <p>
            Email{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
            . We respond within 30 days.
          </p>
        </Section>

        <section className="mt-14 border-t pt-6 text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-2">
          <span>
            Ratecard Technologies Limited —{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
          </span>
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          <Link to="/terms" className="underline">
            Terms of Service
          </Link>
        </section>
      </div>
    </div>
  );
}
